import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotify } from 'react-admin';
import { apiGet, apiDelete, ApiClientError } from '../utils';

interface DuplicateRow {
  id: string;
  entity_type: string;
  category: string;
  display_name: string;
  release_date: string | null;
  url: string | null;
  wikidata_id: string | null;
  created_at: string;
  updated_at: string;
}

interface DuplicatesResponse {
  data: DuplicateRow[];
}

interface DuplicateGroup {
  key: string;
  displayName: string;
  rows: DuplicateRow[];
}

function groupDuplicates(rows: DuplicateRow[]): DuplicateGroup[] {
  const map = new Map<string, DuplicateRow[]>();
  for (const row of rows) {
    const key = row.display_name.toLowerCase();
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }
  return Array.from(map.entries()).map(([key, rows]) => ({
    key,
    displayName: rows[0].display_name,
    rows,
  }));
}

export function Duplicates() {
  const notify = useNotify();
  const [entityType, setEntityType] = useState('movie');
  const [category, setCategory] = useState('english');
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { entity_type: entityType };
      if (category) params.category = category;
      const resp = await apiGet<DuplicatesResponse>('/admin/api/duplicates', params);
      setGroups(groupDuplicates(resp.data));
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch duplicates';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [entityType, category, notify]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handleDelete = async (id: string) => {
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await apiDelete(`/admin/api/entities/${id}`);
      notify('Entity deleted', { type: 'success' });
      // Remove from local state
      setGroups((prev) =>
        prev
          .map((g) => ({ ...g, rows: g.rows.filter((r) => r.id !== id) }))
          .filter((g) => g.rows.length > 1)
      );
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Delete failed';
      notify(message, { type: 'error' });
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleKeep = async (group: DuplicateGroup, keepId: string) => {
    const toDelete = group.rows.filter((r) => r.id !== keepId);
    for (const row of toDelete) {
      await handleDelete(row.id);
    }
  };

  const totalDuplicates = groups.reduce((sum, g) => sum + g.rows.length, 0);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Duplicate Entities
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityType}
                label="Entity Type"
                onChange={(e) => setEntityType(e.target.value)}
              >
                <MenuItem value="movie">Movie</MenuItem>
                <MenuItem value="movie_song">Movie Song</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="american">American</MenuItem>
                <MenuItem value="tamil">Tamil</MenuItem>
                <MenuItem value="hindi">Hindi</MenuItem>
                <MenuItem value="telugu">Telugu</MenuItem>
                <MenuItem value="kannada">Kannada</MenuItem>
                <MenuItem value="malayalam">Malayalam</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDuplicates}
              disabled={loading}
            >
              Refresh
            </Button>

            {!loading && (
              <Typography variant="body2" color="text.secondary">
                {groups.length} duplicate groups ({totalDuplicates} total rows)
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && groups.length === 0 && (
        <Alert severity="success">No duplicates found.</Alert>
      )}

      {!loading &&
        groups.map((group) => (
          <Card key={group.key} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {group.displayName}
                <Chip label={`${group.rows.length} copies`} size="small" sx={{ ml: 1 }} />
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Release Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Wikipedia</TableCell>
                      <TableCell>Wikidata ID</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.release_date || '—'}</TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(row.updated_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {row.url ? (
                            <Link href={row.url} target="_blank" rel="noopener">
                              Link
                            </Link>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{row.wikidata_id || '—'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckIcon />}
                            onClick={() => handleKeep(group, row.id)}
                            disabled={deletingIds.has(row.id)}
                            sx={{ mr: 1 }}
                          >
                            Keep
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(row.id)}
                            disabled={deletingIds.has(row.id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))}
    </Box>
  );
}
